// src/pages/usuarios/Usuarios.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext } from "react";
import { toast } from '../../utils/toast';
import { Box, Button, IconButton, Tooltip, Stack, Paper, Typography, alpha } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridToolbar, esES } from "@mui/x-data-grid";
import PageLayout from "../../components/layout/PageLayout";
import { UsuariosApi } from "../../services/api/UsuariosApi";
import PersonIcon from "@mui/icons-material/Person";
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon } from "@mui/icons-material";
import Swal from "sweetalert2";
import { FormularioUsuario } from "../../components/module/usuarios/FormularioUsuario";
import { CreateUsuarioDto } from "../../components/types/usuario.types";
import { DarkModeContext } from "../../context/darkModeContext";

const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB", error: "#E03E3E", errorBg: "#FBE4E4", purple: "#6940A5", purpleBg: "#EAE4F2", hover: "rgba(55,53,47,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A", error: "#D44C47", errorBg: "#3D2020", purple: "#9A6DD7", purpleBg: "#2A2040", hover: "rgba(255,255,255,0.04)", shadow: "0 1px 3px rgba(0,0,0,0.2)" },
};

export interface Usuario { Id: number; IdEmpleado?: number; IdPerfil: number; IdRol: number; Nombre?: string; Usuario: string; Activo: boolean; Eliminado?: boolean; empleado?: { Nombre: string; Apellidos: string }; perfil?: { Id: number; Nombre: string }; rol?: { Id: number; Nombre: string }; }
interface GridRow extends Usuario { id: number; }
export interface UpdateUsuarioDto extends Partial<Omit<CreateUsuarioDto, "Contrasena">> { Contrasena?: string; }

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState<GridRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [selectedUser, setSelectedUser] = useState<Usuario | null>(null);
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  useEffect(() => { cargarUsuarios(); }, []);

  const cargarUsuarios = async () => {
    try { setLoading(true); const data = await UsuariosApi.findAll(); setUsuarios(data.map((u: any) => ({ ...u, id: u.Id }))); }
    catch { toast.error("No se pudieron cargar los usuarios"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    const r = await Swal.fire({ title: "¿Eliminar usuario?", text: "Esta acción es permanente", icon: "warning", showCancelButton: true, confirmButtonText: "Eliminar", cancelButtonText: "Cancelar", confirmButtonColor: p.error, reverseButtons: true });
    if (r.isConfirmed) { await UsuariosApi.softDelete(id); await cargarUsuarios(); toast.success("Eliminado"); }
  };

  const columns: GridColDef[] = [
    { field: "Id", headerName: "ID", width: 70 },
    { field: "Usuario", headerName: "Usuario", flex: 1, minWidth: 150, renderCell: (params) => (
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", fontWeight: 700, color: p.accent }}>{(params.value || "U")[0]}</Box>
        <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text }}>{params.value}</Typography>
      </Stack>
    )},
    { field: "Nombre", headerName: "Nombre", flex: 1, minWidth: 150 },
    { field: "nombreCompleto", headerName: "Empleado", flex: 1, minWidth: 180, valueGetter: (params) => params.row.empleado ? `${params.row.empleado.Nombre} ${params.row.empleado.Apellidos}` : "—" },
    { field: "perfil", headerName: "Perfil", width: 140, renderCell: (params: GridRenderCellParams) => (
      <Box sx={{ display: "inline-flex", px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: p.purpleBg, color: p.purple, fontSize: "0.78rem", fontWeight: 600 }}>{params.row.perfil?.Nombre || "—"}</Box>
    )},
    { field: "rol", headerName: "Rol", width: 140, renderCell: (params: GridRenderCellParams) => (
      <Box sx={{ display: "inline-flex", px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: p.accentBg, color: p.accent, fontSize: "0.78rem", fontWeight: 600 }}>{params.row.rol?.Nombre || "—"}</Box>
    )},
    { field: "Activo", headerName: "Estado", width: 110, renderCell: (params: GridRenderCellParams) => {
      const activo = params.row.Activo;
      return <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.2, py: 0.3, borderRadius: "6px", bgcolor: activo ? p.successBg : p.errorBg, color: activo ? p.success : p.error, fontSize: "0.78rem", fontWeight: 600 }}>
        <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: activo ? p.success : p.error }} />{activo ? "Activo" : "Inactivo"}
      </Box>;
    }},
    { field: "actions", headerName: "", width: 100, sortable: false, renderCell: (params: GridRenderCellParams) => (
      <Stack direction="row" spacing={0.5}>
        <Tooltip title="Editar"><IconButton size="small" onClick={() => { setSelectedUser(params.row); setOpenForm(true); }} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.accent, bgcolor: p.accentBg, "&:hover": { bgcolor: alpha(p.accent, 0.15) } }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
        <Tooltip title="Eliminar"><IconButton size="small" onClick={() => handleDelete(params.row.Id)} sx={{ width: 30, height: 30, borderRadius: "7px", color: p.error, bgcolor: p.errorBg, "&:hover": { bgcolor: alpha(p.error, 0.15) } }}><DeleteIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
      </Stack>
    )},
  ];

  const handleSubmit = async (values: CreateUsuarioDto) => {
    try {
      if (selectedUser) { await UsuariosApi.update(selectedUser.Id, { ...values, Eliminado: false } as any); }
      else { await UsuariosApi.create(values); }
      await cargarUsuarios(); setOpenForm(false);
      toast.success(`Usuario ${selectedUser ? "actualizado" : "creado"}`);
    } catch { toast.error("No se pudo guardar el usuario"); }
  };

  return (
    <PageLayout icon={PersonIcon} title="Usuarios">
        <Box sx={{ p: 3 }}>
          <Paper elevation={0} sx={{ border: `1px solid ${p.border}`, borderRadius: "12px", overflow: "hidden", bgcolor: p.bg, boxShadow: p.shadow }}>
            <Box sx={{ px: 3, pt: 2.5, pb: 0, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text }}>Gestión de Usuarios</Typography>
                <Typography sx={{ fontSize: "0.82rem", color: p.textT, mt: 0.3 }}>{usuarios.length} usuarios registrados</Typography>
              </Box>
              <Button variant="contained" disableElevation startIcon={<AddIcon />} onClick={() => { setSelectedUser(null); setOpenForm(true); }}
                sx={{ bgcolor: p.accent, color: "#FFF", px: 2.5, py: 1, fontSize: "0.84rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
                Nuevo Usuario
              </Button>
            </Box>
            <Box sx={{ px: 1, pt: 1 }}>
              <DataGrid rows={usuarios} columns={columns} getRowId={(row) => row.Id} loading={loading} pageSize={10} rowsPerPageOptions={[5, 10, 20, 50]} pagination autoHeight
                checkboxSelection={false} disableSelectionOnClick
                components={{ Toolbar: GridToolbar }}
                componentsProps={{ toolbar: { showQuickFilter: true, quickFilterProps: { debounceMs: 500, placeholder: "Buscar usuarios...", sx: { "& .MuiInputBase-root": { fontSize: "0.84rem", borderRadius: "8px", bgcolor: p.bgS, border: `1px solid ${p.border}`, px: 1.5, "&:hover": { borderColor: p.borderH }, "&.Mui-focused": { borderColor: p.accent }, "& fieldset": { border: "none" } } } } } }}
                localeText={esES.components.MuiDataGrid.defaultProps.localeText}
                sx={{
                  border: "none", fontSize: "0.84rem",
                  "& .MuiDataGrid-columnHeaders": { bgcolor: p.bgS, borderBottom: `1px solid ${p.border}`, borderTop: `1px solid ${p.border}`, minHeight: "44px !important", maxHeight: "44px !important" },
                  "& .MuiDataGrid-columnHeaderTitle": { fontSize: "0.78rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em" },
                  "& .MuiDataGrid-columnSeparator": { display: "none" },
                  "& .MuiDataGrid-row": { borderBottom: `1px solid ${p.border}`, "&:hover": { bgcolor: p.hover } },
                  "& .MuiDataGrid-cell": { borderBottom: "none", py: 1 },
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": { outline: "none" },
                  "& .MuiDataGrid-toolbarContainer": { py: 1.5, px: 2, gap: 1, "& .MuiButton-root": { fontSize: "0.78rem", color: p.textS, fontWeight: 500 } },
                  "& .MuiDataGrid-footerContainer": { borderTop: `1px solid ${p.border}`, minHeight: 48 },
                }}
              />
            </Box>
          </Paper>
          <FormularioUsuario open={openForm} onClose={() => setOpenForm(false)} onSubmit={handleSubmit} initialValues={selectedUser || undefined} isEdit={!!selectedUser} />
        </Box>
      </PageLayout>
  );
};

export default Usuarios;
